import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import usersApi from "../../api/users";

/**
 * Generic shape — we store whatever the backend returns and let the
 * consumer (Home page) decide how to filter against `config/modules.ts`.
 * Refine the type once the response shape is locked in.
 */
export type AccessibleModule = any;

interface State {
  data: AccessibleModule[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: State = {
  data: null,
  loading: false,
  error: null,
};

export const fetchAccessibleModules = createAsyncThunk(
  "accessibleModules/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await usersApi.getUserAccessibleModules();
      // Gateway shape: { status, message, response: [{ appType, appPath }] }
      if (Array.isArray(res?.response)) return res.response;
      if (Array.isArray(res?.data)) return res.data;
      if (Array.isArray(res)) return res;
      return [];
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load accessible modules"
      );
    }
  }
);

const slice = createSlice({
  name: "accessibleModules",
  initialState,
  reducers: {
    clearAccessibleModules: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccessibleModules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccessibleModules.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload as AccessibleModule[];
      })
      .addCase(fetchAccessibleModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearAccessibleModules } = slice.actions;
export default slice.reducer;
